import { FC, useState, useEffect } from 'react';
import { Card } from './Card';
import { Button } from './Button';
import { cn } from '@/utils/cn';
import { faqApi } from '@/services/api';

interface FAQItem {
  id: string;
  category: string;
  question: string;
  answer: string;
  keywords?: string[];
  viewCount?: number;
}

interface CategoryFAQProps {
  className?: string;
}

export const CategoryFAQ: FC<CategoryFAQProps> = ({ className = '' }) => {
  const [faqs, setFaqs] = useState<FAQItem[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // 分类图标映射
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case '异常情况':
        return '🚨';
      case '实用技巧':
        return '💡';
      case '售前问题':
        return '💬';
      default:
        return '❓';
    }
  };

  // 分类颜色映射
  const getCategoryColor = (category: string) => {
    switch (category) {
      case '异常情况':
        return 'from-red-50 to-orange-50 border-red-200 text-red-800';
      case '实用技巧':
        return 'from-blue-50 to-cyan-50 border-blue-200 text-blue-800';
      case '售前问题':
        return 'from-green-50 to-emerald-50 border-green-200 text-green-800';
      default:
        return 'from-gray-50 to-slate-50 border-gray-200 text-gray-800';
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // 并行获取FAQ列表和分类
        const [faqResponse, categoriesResponse] = await Promise.all([
          faqApi.getPublicFAQList(),
          faqApi.getFAQCategories()
        ]);
        
        // 转换FAQ数据格式
        const faqItems: FAQItem[] = (faqResponse || []).map(faq => ({
          id: faq.id,
          category: faq.category,
          question: faq.question,
          answer: faq.answer,
          keywords: faq.keywords ? JSON.parse(faq.keywords) : [],
          viewCount: 0 // FAQPublicResponse没有view_count字段，设为0
        }));
        
        setFaqs(faqItems);
        setCategories(categoriesResponse || []);
        
        // 如果有分类数据，默认选中第一个分类
        if (categoriesResponse && categoriesResponse.length > 0) {
          setSelectedCategory(categoriesResponse[0]);
        }
      } catch (err) {
        console.error('Failed to fetch FAQ data:', err);
        setError('加载常见问题失败，请稍后重试');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const toggleFAQ = async (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
    
    // 增加查看次数
    const currentFAQ = filteredFAQs[index];
    if (openIndex !== index && currentFAQ?.id) {
      try {
        await faqApi.incrementFAQViewCount(currentFAQ.id);
      } catch (err) {
        console.error('Failed to increment FAQ view count:', err);
      }
    }
  };

  // 筛选FAQ
  const filteredFAQs = faqs.filter(faq => {
    const matchesCategory = !selectedCategory || faq.category === selectedCategory;
    const matchesSearch = !searchTerm || 
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (faq.keywords || []).some(keyword => 
        keyword.toLowerCase().includes(searchTerm.toLowerCase())
      );
    return matchesCategory && matchesSearch;
  });

  // 按分类分组FAQ
  const faqsByCategory = categories.reduce((acc, category) => {
    acc[category] = faqs.filter(faq => faq.category === category);
    return acc;
  }, {} as Record<string, FAQItem[]>);

  // 获取分类统计
  const getCategoryCount = (category: string) => {
    return faqsByCategory[category]?.length || 0;
  };

  if (loading) {
    return (
      <div className={className}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {[1, 2, 3].map((n) => (
            <div key={n} className="animate-pulse">
              <div className="h-20 bg-gray-200 rounded-lg"></div>
            </div>
          ))}
        </div>
        <div className="space-y-4">
          {[1, 2, 3, 4].map((n) => (
            <Card key={n} className="animate-pulse">
              <div className="h-6 bg-gray-200 rounded mb-2"></div>
              <div className="h-4 bg-gray-100 rounded w-3/4"></div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error && faqs.length === 0) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <div className="text-6xl mb-4">😕</div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">加载失败</h3>
        <p className="text-gray-600 mb-6">{error}</p>
        <Button
          onClick={() => window.location.reload()}
          className="bg-primary-600 hover:bg-primary-700 text-white"
        >
          重新加载
        </Button>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* 分类导航 */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">问题分类</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {categories.map((category) => {
            const count = getCategoryCount(category);
            const isSelected = selectedCategory === category;
            
            return (
              <button
                key={category}
                onClick={() => {
                  setSelectedCategory(isSelected ? '' : category);
                  setOpenIndex(null); // 切换分类时关闭展开的FAQ
                }}
                className={cn(
                  'p-6 rounded-xl border-2 transition-all duration-200 text-left group',
                  'hover:shadow-lg hover:transform hover:-translate-y-1',
                  isSelected 
                    ? `bg-gradient-to-br ${getCategoryColor(category)} border-current shadow-lg transform -translate-y-1`
                    : 'bg-white border-gray-200 hover:border-gray-300'
                )}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-3xl">{getCategoryIcon(category)}</span>
                  <span className={cn(
                    'text-sm px-2 py-1 rounded-full font-medium',
                    isSelected 
                      ? 'bg-white bg-opacity-60' 
                      : 'bg-gray-100 text-gray-600 group-hover:bg-gray-200'
                  )}>
                    {count} 个问题
                  </span>
                </div>
                <h3 className={cn(
                  'font-semibold text-lg mb-1',
                  isSelected ? 'text-current' : 'text-gray-900'
                )}>
                  {category}
                </h3>
                <p className={cn(
                  'text-sm',
                  isSelected ? 'text-current opacity-80' : 'text-gray-600'
                )}>
                  {category === '异常情况' && '解决使用过程中遇到的各种问题'}
                  {category === '实用技巧' && '提升使用效率的技巧和方法'}
                  {category === '售前问题' && '购买前的疑问和服务咨询'}
                </p>
              </button>
            );
          })}
        </div>

        {/* 全部分类按钮 */}
        <div className="text-center">
          <button
            onClick={() => {
              setSelectedCategory('');
              setOpenIndex(null);
            }}
            className={cn(
              'px-6 py-2 rounded-full transition-all duration-200 font-medium',
              !selectedCategory 
                ? 'bg-primary-600 text-white shadow-lg' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            )}
          >
            查看全部分类 ({faqs.length} 个问题)
          </button>
        </div>
      </div>

      {/* 搜索框 */}
      <div className="mb-6">
        <div className="relative max-w-md mx-auto">
          <input
            type="text"
            placeholder="搜索问题..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
          />
          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            🔍
          </span>
        </div>
      </div>

      {/* FAQ列表 */}
      <div>
        {selectedCategory && (
          <div className="mb-6">
            <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <span>{getCategoryIcon(selectedCategory)}</span>
              {selectedCategory}
              <span className="text-sm text-gray-500 font-normal">
                ({filteredFAQs.length} 个问题)
              </span>
            </h3>
          </div>
        )}

        {filteredFAQs.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🤔</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {searchTerm ? '没有找到相关问题' : '暂无问题'}
            </h3>
            <p className="text-gray-600">
              {searchTerm ? '请尝试使用其他关键词搜索' : '该分类下暂时没有问题'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredFAQs.map((faq, index) => (
              <Card key={faq.id} className="cursor-pointer overflow-hidden" hover={false}>
                <button
                  className="w-full text-left"
                  onClick={() => toggleFAQ(index)}
                >
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">{getCategoryIcon(faq.category)}</span>
                        <span className={cn(
                          'text-xs px-2 py-1 rounded-full font-medium',
                          `bg-gradient-to-r ${getCategoryColor(faq.category).split(' ')[0]} ${getCategoryColor(faq.category).split(' ')[1]}`
                        )}>
                          {faq.category}
                        </span>
                        {faq.viewCount && faq.viewCount > 0 && (
                          <span className="text-xs text-gray-500">
                            👁 {faq.viewCount} 次查看
                          </span>
                        )}
                      </div>
                      <h4 className="font-semibold text-gray-900 text-lg leading-tight">
                        {faq.question}
                      </h4>
                    </div>
                    <span className={cn(
                      'text-primary-600 transition-transform duration-200 text-xl flex-shrink-0 mt-1',
                      openIndex === index ? 'transform rotate-180' : ''
                    )}>
                      ▼
                    </span>
                  </div>
                </button>
                
                <div className={cn(
                  'overflow-hidden transition-all duration-300',
                  openIndex === index ? 'max-h-96 opacity-100 mt-4' : 'max-h-0 opacity-0'
                )}>
                  <div className="border-t border-gray-100 pt-4">
                    <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                      {faq.answer}
                    </p>
                    {faq.keywords && faq.keywords.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {faq.keywords.map((keyword, idx) => (
                          <span
                            key={idx}
                            className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded"
                          >
                            #{keyword}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};